import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/config/api";
import { Plus, Edit, ChevronRight, Search } from "lucide-react";
import { TablePagination } from "@/components/TablePagination";
import NewCustomer from "./NewCustomer";

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [search, setSearch] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [balanceFilter, setBalanceFilter] = useState("all");
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem("customers.visibleColumns");
      return saved
        ? JSON.parse(saved)
        : {
            company: true,
            email: true,
            phone: true,
            amountToReceive: true,
            openingBalance: true,
            totalInvoiced: true,
            paid: true,
            openOrders: true,
            lastInvoice: true,
          };
    } catch {
      return {
        company: true,
        email: true,
        phone: true,
        amountToReceive: true,
        openingBalance: true,
        totalInvoiced: true,
        paid: true,
        openOrders: true,
        lastInvoice: true,
      };
    }
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    localStorage.setItem("customers.visibleColumns", JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const fetchCustomers = async () => {
    try {
      const data = await api.customers.getAll();
      setCustomers(data);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewCustomerSuccess = () => {
    setShowNewCustomer(false);
    setEditingCustomer(null);
    fetchCustomers();
  };

  const handleEditCustomer = async (customerId) => {
    try {
      const customerData = await api.customers.getById(customerId);
      setEditingCustomer(customerData);
      setShowNewCustomer(true);
    } catch (error) {
      console.error("Failed to fetch customer:", error);
    }
  };

  const filtered = useMemo(() => {
    let list = [...customers];
    const s = (search || "").toLowerCase().trim();
    if (s) {
      list = list.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(s) ||
          (c.email || "").toLowerCase().includes(s) ||
          (c.phone || "").toLowerCase().includes(s) ||
          (c.company || "").toLowerCase().includes(s) ||
          (c.address || "").toLowerCase().includes(s)
      );
    }
    const fc = (filterCompany || "").toLowerCase().trim();
    if (fc) {
      list = list.filter((c) => (c.company || "").toLowerCase().includes(fc));
    }
    if (balanceFilter === "receivable") {
      list = list.filter((c) => Number(c.amount_to_receive || 0) > 0);
    } else if (balanceFilter === "clear") {
      list = list.filter((c) => Number(c.amount_to_receive || 0) <= 0);
    }
    return list;
  }, [customers, search, filterCompany, balanceFilter]);

  const companies = useMemo(() => {
    const set = new Set();
    customers.forEach((c) => {
      if (c.company && c.company.trim()) set.add(c.company.trim());
    });
    return Array.from(set).sort();
  }, [customers]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageStart = (page - 1) * pageSize;
  const paginated = useMemo(
    () => filtered.slice(pageStart, pageStart + pageSize),
    [filtered, pageStart, pageSize],
  );

  useEffect(() => {
    setPage(1);
  }, [search, filterCompany, balanceFilter, pageSize]);

  const columnCount =
    2 + Object.values(visibleColumns).filter(Boolean).length;

  const columnOptions = [
    ["company", "Company"],
    ["email", "Email"],
    ["phone", "Phone"],
    ["amountToReceive", "Amount to Receive"],
    ["openingBalance", "Opening Balance"],
    ["totalInvoiced", "Total Invoiced"],
    ["paid", "Paid Amount"],
    ["openOrders", "Open Orders"],
    ["lastInvoice", "Last Invoice"],
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (showNewCustomer) {
    return (
      <NewCustomer
        customer={editingCustomer}
        onClose={() => {
          setShowNewCustomer(false);
          setEditingCustomer(null);
        }}
        onSuccess={handleNewCustomerSuccess}
      />
    );
  }

  return (
    <div className="space-y-6 min-w-0 max-w-full">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h1 className="text-2xl font-heading font-semibold text-secondary">
          Customers
        </h1>
        <button
          onClick={() => setShowNewCustomer(true)}
          className="bg-primary hover:bg-primary/90 text-white font-medium rounded-lg px-4 py-2 shadow-sm hover:shadow-md transition-all flex items-center gap-2"
        >
          <Plus size={20} />
          New Customer
        </button>
      </div>

      {/* Search and filters */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              placeholder="Search by name, email, phone, company, address..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            />
          </div>
          <div className="w-48">
            <select
              value={filterCompany}
              onChange={(e) => {
                setFilterCompany(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            >
              <option value="">All companies</option>
              {companies.map((co) => (
                <option key={co} value={co}>
                  {co}
                </option>
              ))}
            </select>
          </div>
          <div className="w-52">
            <select
              value={balanceFilter}
              onChange={(e) => {
                setBalanceFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            >
              <option value="all">All balances</option>
              <option value="receivable">Amount to receive</option>
              <option value="clear">No receivable</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-3 text-xs text-stone-600">
          {columnOptions.map(([key, label]) => (
            <label key={key} className="inline-flex items-center gap-1">
              <input
                type="checkbox"
                checked={visibleColumns[key]}
                onChange={(e) =>
                  setVisibleColumns((prev) => ({
                    ...prev,
                    [key]: e.target.checked,
                  }))
                }
              />
              {label}
            </label>
          ))}
        </div>
        <p className="form-helper mt-2">
          Showing {filtered.length} of {customers.length} customer(s)
        </p>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Name</th>
                {visibleColumns.company && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Company</th>}
                {visibleColumns.email && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Email</th>}
                {visibleColumns.phone && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Phone</th>}
                {visibleColumns.amountToReceive && <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Amount to Receive</th>}
                {visibleColumns.openingBalance && <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Opening Balance</th>}
                {visibleColumns.totalInvoiced && <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Total Invoiced</th>}
                {visibleColumns.paid && <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Paid</th>}
                {visibleColumns.openOrders && <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Open Orders</th>}
                {visibleColumns.lastInvoice && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Last Invoice</th>}
                <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="py-12 text-center text-stone-500">
                    {customers.length === 0
                      ? "No customers yet. Add your first customer."
                      : "No customers match your search or filter."}
                  </td>
                </tr>
              ) : (
                paginated.map((customer) => (
                  <tr
                    key={customer.id}
                    className="border-b border-stone-100 hover:bg-stone-50/50 cursor-pointer"
                    onClick={() => navigate(`/customers/${customer.id}`)}
                  >
                    <td className="py-3 px-4">
                      <span className="font-medium text-secondary">{customer.name}</span>
                    </td>
                    {visibleColumns.company && <td className="py-3 px-4 text-sm text-stone-600">
                      {customer.company || "—"}
                    </td>}
                    {visibleColumns.email && <td className="py-3 px-4 text-sm text-stone-600">
                      {customer.email || "—"}
                    </td>}
                    {visibleColumns.phone && <td className="py-3 px-4 text-sm text-stone-600">
                      {customer.phone || "—"}
                    </td>}
                    {visibleColumns.amountToReceive && <td className="py-3 px-4 text-right font-mono text-sm font-semibold text-amber-600">
                      {Number(customer.amount_to_receive || 0).toFixed(2)}
                    </td>}
                    {visibleColumns.openingBalance && <td className="py-3 px-4 text-right font-mono text-sm text-stone-700">
                      {Number(customer.opening_balance || 0).toFixed(2)}
                    </td>}
                    {visibleColumns.totalInvoiced && <td className="py-3 px-4 text-right font-mono text-sm text-stone-700">
                      {Number(customer.total_invoiced || 0).toFixed(2)}
                    </td>}
                    {visibleColumns.paid && <td className="py-3 px-4 text-right font-mono text-sm text-emerald-600">
                      {Number(customer.amount_paid || 0).toFixed(2)}
                    </td>}
                    {visibleColumns.openOrders && <td className="py-3 px-4 text-right text-sm text-stone-700">
                      {customer.open_orders_count || 0}
                    </td>}
                    {visibleColumns.lastInvoice && <td className="py-3 px-4 text-sm text-stone-600">
                      {customer.last_invoice_date
                        ? new Date(customer.last_invoice_date).toLocaleDateString()
                        : "—"}
                    </td>}
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditCustomer(customer.id);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/customers/${customer.id}`);
                          }}
                          className="p-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
                          title="View"
                        >
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <TablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={filtered.length}
            pageSize={pageSize}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        )}
      </div>
    </div>
  );
}
